import { I18n } from '@/i18n/I18n';
import { CompletionContext, CompletionResult } from '@codemirror/autocomplete';
import { Diagnostic, linter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';

export const SPECIAL_INSTRUMENTS = {
  VERIFORM: 'veriform',
  RESOURCERY: 'resourcery'
};

export enum VERIFORM_METHODS {
  FILE_OUTPUT = 'FileOutput',
  IMAGE_OUTPUT = 'ImageOutput',
  TEXT_OUTPUT = 'TextOutput',
  VIDEO_OUTPUT = 'VideoOutput',
  TEXT = 'Text',
  FILE = 'File',
  VIDEO = 'Video',
  IMAGE = 'Image',
  DELAY = 'Delay',
}

export function customCompletion() {
  const customCompletionData = (context: CompletionContext): CompletionResult | null => {
    const word = context.matchBefore(/\b\w+\.?$/);
    if (!word) return null;

    if (word.text === `${SPECIAL_INSTRUMENTS.VERIFORM}.`) {
      return veriformCompletion(word);
    }

    return null;
  };
  return customCompletionData;
}
export const unquotedBracesLinter = linter((view: EditorView): Diagnostic[] => {
  const diagnostics: Diagnostic[] = [];

  // Regex to match {{...}} patterns
  const regex = /\{\{[^{}]*\}\}/g;
  const text = view.state.doc.toString();
  let match: RegExpExecArray | null;

  // Helper function to check if the match is in a standalone value context
  const isStandaloneValue = (standAloneText: string, index: number): boolean => {
    const beforeText = standAloneText.slice(0, index).trim();
    const afterText = standAloneText.slice(index + match![0].length).trim();

    // If there's no other text around the {{...}} (i.e., it's standalone), flag it
    if (/^[:\s]*$/.test(beforeText) && /^$|[\r\n]/.test(afterText)) {
      return true;
    }

    return false;
  };

  while ((match = regex.exec(text)) !== null) {
    const currentMatch = match[0];
    const {index} = match;

    // Only flag if the {{...}} pattern is standalone (e.g., not part of a larger string)
    if (isStandaloneValue(text, index)) {
      diagnostics.push({
        from: index,
        to: index + currentMatch.length,
        severity: 'error',
        message: 'Standalone {{...}} templates should be enclosed in quotes.',
        actions: [{
          name: 'Fix',
          apply(newView, from, to) {
            newView.dispatch({
              changes: { from, to, insert: `'${currentMatch}'` } // Wrap the standalone template in single quotes
            });
          }
        }]
      });
    }
  }

  return diagnostics;
});

export function lintMethodNames(view: EditorView): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const text = view.state.doc.toString();

  const keyRegex = /^\s*(?:-\s*)?([^\s:]+):/gm;

  let match = keyRegex.exec(text);
  while (match !== null) {
    const fullMatch = match[0];
    const key = match[1];
    const keyStartIndex = match.index + fullMatch.indexOf(key);
    const keyEndIndex = keyStartIndex + key.length;

    const dotIndex = key.indexOf('.');
    if (dotIndex !== -1 && dotIndex < key.length - 1) {
      const methodName = key.substring(dotIndex + 1);

      if (!/^[A-Z]/.test(methodName)) {
        diagnostics.push({
          from: keyStartIndex + dotIndex + 1,
          to: keyEndIndex,
          severity: 'error',
          message: I18n.get('methodMustStartWithCapital'),
          actions: [
            {
              name: 'Capitalize',
              apply(editorView, from, to) {
                const capitalizedMethod =
                  methodName.charAt(0).toUpperCase() + methodName.slice(1);
                editorView.dispatch({
                  changes: { from, to, insert: capitalizedMethod },
                });
              },
            },
          ],
        });
      }
    }

    match = keyRegex.exec(text);
  }

  return diagnostics;
}

export const methodNameLinter = linter(lintMethodNames);

function veriformCompletion(word: {from: number, to: number, text: string}): CompletionResult {
  return {
    from: word.to,
    options: [
      {label: VERIFORM_METHODS.TEXT, type: 'text', apply: `Text:
    content: ''`},
      {label: VERIFORM_METHODS.FILE, type: 'text', apply: `File:
    name: ''
    source: ''`},
      {label: VERIFORM_METHODS.VIDEO, type: 'text', apply: `Video:
    path: ''`},
      {label: VERIFORM_METHODS.IMAGE, type: 'text', apply: `Image:
    path: ''`},
    {label: VERIFORM_METHODS.DELAY, type: 'text', apply: `Delay:
      second: 1`},
    ],
  };
}
